import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { keyframes } from 'styled-components'
import { colors, spacing } from 'src/constants/marketing-ds'

const DEFAULT_ANIMATION_DURATION = 300
const ANIMATION_BUFFER_TIME = 100

const slideOutDown = keyframes({
  '0%': {
    transform: 'translateY(0%)',
    opacity: 1,
  },

  '100%': {
    transform: 'translateY(100%)',
    opacity: 0,
  },
})

const slideInDown = keyframes({
  '0%': {
    transform: 'translateY(-100%)',
    opacity: 0,
  },

  '100%': {
    transform: 'translateY(0%)',
    opacity: 1,
  },
})

export function AnimatedDigit({
  digit,
  isStartingSoon,
  animationDuration = DEFAULT_ANIMATION_DURATION,
}) {
  const [displayDigit, setDisplayDigit] = useState(digit)
  const [animatingFrom, setAnimatingFrom] = useState(null)
  const [animatingTo, setAnimatingTo] = useState(null)
  const previousDigit = useRef(digit)
  const currentDigitRef = useRef(null)
  const nextDigitRef = useRef(null)
  const fallbackTimeoutRef = useRef(null)
  const animationIdRef = useRef(0)

  useEffect(() => {
    if (digit === previousDigit.current) {
      return
    }

    animationIdRef.current += 1
    const currentAnimationId = animationIdRef.current

    // Clear any existing timeout to prevent race conditions
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = null
    }

    setAnimatingFrom(previousDigit.current)
    setAnimatingTo(digit)

    // Don't update displayDigit immediately - let animation handle it
    previousDigit.current = digit

    fallbackTimeoutRef.current = setTimeout(() => {
      // Only proceed if this timeout corresponds to the latest animation
      if (currentAnimationId === animationIdRef.current) {
        setDisplayDigit(digit)
        setAnimatingFrom(null)
        setAnimatingTo(null)
        fallbackTimeoutRef.current = null
      }
    }, animationDuration + ANIMATION_BUFFER_TIME)
  }, [digit, animationDuration])

  useEffect(() => {
    const currentEl = currentDigitRef.current
    const nextEl = nextDigitRef.current

    const handleAnimationEnd = (e) => {
      const target = e.target
      if (target === currentEl || target === nextEl) {
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current)
          fallbackTimeoutRef.current = null
        }
        setDisplayDigit(digit)
        setAnimatingFrom(null)
        setAnimatingTo(null)
      }
    }

    if (currentEl) {
      currentEl.addEventListener('animationend', handleAnimationEnd)
    }
    if (nextEl) {
      nextEl.addEventListener('animationend', handleAnimationEnd)
    }

    return () => {
      if (currentEl) {
        currentEl.removeEventListener('animationend', handleAnimationEnd)
      }
      if (nextEl) {
        nextEl.removeEventListener('animationend', handleAnimationEnd)
      }
    }
  }, [animatingFrom, animatingTo])

  useEffect(() => {
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current)
      }
    }
  }, [])

  const isAnimating = animatingFrom !== null && animatingTo !== null

  return (
    <Container>
      {isAnimating ? (
        <>
          <CurrentDigit
            ref={currentDigitRef}
            $animationDuration={animationDuration}
            $isStartingSoon={isStartingSoon}
          >
            {animatingFrom}
          </CurrentDigit>
          <NextDigit
            ref={nextDigitRef}
            $animationDuration={animationDuration}
            $isStartingSoon={isStartingSoon}
          >
            {animatingTo}
          </NextDigit>
        </>
      ) : (
        <StaticDigit $isStartingSoon={isStartingSoon}>{displayDigit}</StaticDigit>
      )}
    </Container>
  )
}

// Styled Components
const Container = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  height: ${spacing.lg};
  width: 20px;
  flex-shrink: 0;
`

const StaticDigit = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${({ $isStartingSoon }) =>
    $isStartingSoon ? colors.yellow[100] : colors.semantic.text.primary};
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
`

const CurrentDigit = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(0%);
  color: ${({ $isStartingSoon }) =>
    $isStartingSoon ? colors.yellow[100] : colors.semantic.text.primary};
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  animation: ${slideOutDown} ${({ $animationDuration }) => $animationDuration}ms
    cubic-bezier(0.4, 0, 0.2, 1);
  animation-delay: ${({ $animationDuration }) => $animationDuration * 0.2}ms;
`

const NextDigit = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transform: translateY(-100%);
  color: ${({ $isStartingSoon }) =>
    $isStartingSoon ? colors.yellow[100] : colors.semantic.text.primary};
  text-align: center;
  font-variant-numeric: lining-nums tabular-nums;
  animation: ${slideInDown} ${({ $animationDuration }) => $animationDuration}ms
    cubic-bezier(0.4, 0, 0.2, 1);
  animation-delay: ${({ $animationDuration }) => $animationDuration * 0.2}ms;
`
